import React, { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import DesignListView from './DesignListView';
import BuilderSelect from "@/store/builderStoreSelector";
import { BuilderDesign, DesignMap, DesignStyle, RoomModel } from '@/store/builderSlice';
import { useDispatch } from 'react-redux';
import { loadDesignStyles } from '@/store/builder/thunks/builderDesignsLoader';
import { Box, CircularProgress } from '@mui/material';
import { BUILDER_SUCCESS } from '@/utils/builder/builderConstants';

const styleNames = [
  'Traditional',
  'Coastal',
  'Rustic',
  'Modern and Contemporary',
  'Modern', 'Contemporary',
  'Casual',
  'Industrial',
  'Transitional',
  'Rustic Farmhouse'
];

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme, ...props }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    fontSize: props!.style!.fontSize,
    color: props!.style!.color
  },
  '& .MuiAccordionSummary-content:hover': {
    color: '#0D908E',
    fontWeight: 'bold',
  }
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const designRoomTypeMap: { [key: string]: string } = {
  'Bedroom': 'bedroom',
  'Bed Room': 'bedroom',
  'Living Room': 'livingroom',
  'Dining Room': 'diningroom',
  'Home Office': 'homeoffice'
}

export default function DesignsTray(props: { builderType?: string, showOnlySelectedDesign?: boolean }) {
  const dispatch = useDispatch();
  const urlParams = BuilderSelect.ui().urlParams;
  const currentRoomId = BuilderSelect.activeSelection().roomId;
  const roomModels: RoomModel[] = BuilderSelect.roomModels().models;
  const designStyles: DesignStyle[] = BuilderSelect.designContent().designStyles;
  const designStylesLoadStatus = BuilderSelect.ui().designStyleLoadStatus;
  let currentStyle = BuilderSelect.ui().styleName;
  const curDesignId = BuilderSelect.ui().designId;
  const currentRoomModel = useMemo(
    () => roomModels.find(roomModel => roomModel.roomId === currentRoomId),
    [roomModels, currentRoomId]
  );
  
  const designMap = BuilderSelect.designContent().designs;
  const roomType = currentRoomModel ? designRoomTypeMap[currentRoomModel.roomType] || currentRoomModel.roomType.toLowerCase() : '';
  let designs: BuilderDesign[] = [];
  const [expanded, setExpanded] = useState<string | false>('');
  let alreadySelectedDesignFromInspirations: any = null;

  const childRef = useRef(null);

  const collapseCurrentStyle = useCallback((collapseStyle: boolean) => {
    if (collapseStyle) {
      setExpanded(false);
    }
  }, []);

  const collapseExpandedView = () => {
    if (childRef.current) {
      //@ts-ignore
      childRef.current.closeExpandedView();
    }
  };

  if (curDesignId) {
    for (let roomName of Object.keys(designMap)) {
      if (roomName.toLowerCase() === roomType) {
        const curRoomDesigns = designMap[roomName];
        for (let vendorName of Object.keys(curRoomDesigns)) {
          const curVendorDesigns = curRoomDesigns[vendorName];
          for (let sName of Object.keys(curVendorDesigns)) {
            designs = designs.concat(curVendorDesigns[sName]);
          }
        }
      }
    }
    const design = designs.find(design => parseInt(design.designId) === parseInt(curDesignId));
    if (design) {
      currentStyle = design.style;
    }
  }

  // const styles = useMemo(() => {
  //   let result = designStyles;
  //   if (currentStyle) {
  //     const filteredStyles = designStyles.filter(designStyle => designStyle.style === currentStyle.toLowerCase());
  //     if (filteredStyles.length > 0) {
  //       alreadySelectedDesignFromInspirations = filteredStyles;
  //       result = filteredStyles;
  //     }
  //   }
  //   return result;
  // }, [designStyles, currentStyle]);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  let styles = designStyles;
  if (currentStyle) {
    const filteredStyles = designStyles.filter(designStyle => designStyle.style === currentStyle.toLowerCase());
    if (filteredStyles.length > 0) {
      // styles = filteredStyles;
      // setExpanded(currentStyle);
      alreadySelectedDesignFromInspirations = filteredStyles;
    }
  }
  
  useEffect(() => {
    if (roomType !== '' && designStylesLoadStatus !== BUILDER_SUCCESS) {
      dispatch(loadDesignStyles({ roomName: roomType }) as any);
    }
  }, [roomType, designStylesLoadStatus]);

  useEffect(() => {
    if (designStylesLoadStatus === BUILDER_SUCCESS && styles.length > 0) {
      if (alreadySelectedDesignFromInspirations !== null) {
        setExpanded(alreadySelectedDesignFromInspirations[0].style);
      } else {
        setExpanded(styles[0].style);
      }
    }
  }, [designStylesLoadStatus, styles]);

  if (!currentRoomModel) return <></>;

  const getStylesAccordion = () => {
    const comps: JSX.Element[] = [];
    styles.map((designStyle) => (
      comps.push(
        <Accordion
          key={designStyle.style}
          expanded={expanded === designStyle.style.toLowerCase()}
          onChange={handleChange(designStyle.style.toLowerCase())}
          style={{ border: 0 }}
        >
          <AccordionSummary
            id="panel1d-header"
            expandIcon={<IconButton style={{ display: 'none' }}><ExpandMoreIcon /></IconButton>}
            style={{
              backgroundColor: 'white',
              fontSize: '14px',
              color: expanded === designStyle.style.toLowerCase() ? '#0D908E' : (expanded === '' || !expanded) ? '#000000' : '#C0C0C0',
              fontWeight: expanded === designStyle.style.toLowerCase() ? 'bold' : 'normal',
              padding: props.builderType === '3d' ? '0px' : '0px 16px'
            }}
          >
            {designStyle.styleDisplayName}
          </AccordionSummary>
          {expanded === designStyle.style.toLowerCase() && (
            <AccordionDetails style={{ padding: 0, border: 0 }}>
              <DesignListView
                roomType={roomType}
                styleName={designStyle.style.toLowerCase()}
                closeExpandedViewCallback={collapseCurrentStyle}
                ref={childRef}
              />
            </AccordionDetails>)
          }
        </Accordion>
      )
    ));
    //return props.showOnlySelectedDesign ? comps[0] : comps;
    return props.showOnlySelectedDesign ? comps.filter((eachComp: any) => eachComp.key == currentStyle) : comps;
  };

  return (
    <Box component={'div'} sx={{
      maxWidth: props.builderType === '3d' ? '100%' : 120,
      maxHeight: props.builderType === '3d' ? '100%' : 'calc(100vh - 200px)',
      overflowY: 'auto'
    }}>
      {props.builderType !== '3d' && (
        <Box component={'div'} visibility={(expanded === '' || !expanded) ? 'hidden' : 'visible'}>
          <NavigateBeforeOutlinedIcon
            style={{ color: 'black', fontSize: '24px', cursor: 'pointer' }}
            onClick={collapseExpandedView}
          />
        </Box>
      )}
      {designStylesLoadStatus === '' ? <CircularProgress /> : getStylesAccordion()}
    </Box>
  );
}